import { useMemo, useState } from "react";
import { Modal, Form, Input, Button, Select, Switch, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  useMoveToConfirmedMutation,
  useMoveToProcessingMutation,
  useMoveToAvailableMutation,
  useMoveToPickupMutation,
  useMoveToDeliveryMutation,
} from "@/services/api/orders/ordersApi";
import { useGetDriversQuery } from "@/services/api/driver/driverApi";
import useAuth from "@/hooks/useAuth";

const { TextArea } = Input;

const BulkFulfillmentModal = ({
  open,
  onClose,
  selectedOrderIds,
  currentStage,
  showAddDriverModal,
  // onShowDriverDialog,
}) => {
  const [form] = Form.useForm();
  const user = useAuth();
  const [useInhouseDriver, setUseInhouseDriver] = useState(true);

  const { data: driversData, isLoading: driversLoading } = useGetDriversQuery();

  // Mutations
  const [moveToConfirmed, { isLoading: movingToConfirmed }] =
    useMoveToConfirmedMutation();
  const [moveToProcessing, { isLoading: movingToProcessing }] =
    useMoveToProcessingMutation();
  const [moveToAvailable, { isLoading: movingToAvailable }] =
    useMoveToAvailableMutation();
  const [moveToPickup, { isLoading: movingToPickup }] =
    useMoveToPickupMutation();
  const [moveToDelivery, { isLoading: movingToDelivery }] =
    useMoveToDeliveryMutation();

  // Get active drivers
  const activeDrivers = useMemo(() => {
    if (!driversData) return [];
    return driversData.filter((driver) => driver.status === true);
  }, [driversData]);

  const isLoading =
    movingToConfirmed ||
    movingToProcessing ||
    movingToAvailable ||
    movingToPickup ||
    movingToDelivery;

  // Stage configuration
  const stageConfig = {
    1: {
      title: "Move to Confirmed",
      nextStage: "Confirmed",
      mutation: moveToConfirmed,
    },
    2: {
      title: "Move to Processing",
      nextStage: "Processing",
      mutation: moveToProcessing,
    },
    3: {
      title: "Move to Available",
      nextStage: "Available",
      mutation: moveToAvailable,
    },
    4: {
      title: "Move to Pickup",
      nextStage: "Pickup",
      mutation: moveToPickup,
      requiresDriver: true,
    },
    5: {
      title: "Move to Delivery",
      nextStage: "Delivery",
      mutation: moveToDelivery,
    },
  };

  const config = stageConfig[currentStage];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      let payload = {
        order_ids: selectedOrderIds,
        created_by: user?.id,
        comment: values.comment,
      };

      // Special handling for pickup stage
      if (config?.requiresDriver) {
        if (useInhouseDriver) {
          payload.is_house_driver = true;
          payload.driver_id = values.driver_id;
        } else {
          payload.is_house_driver = false;
          payload.driver_name = values.driver_name;
          payload.driver_phone_no = values.driver_phone_no;
        }
      }

      await config.mutation(payload).unwrap();
      message.success(
        `${selectedOrderIds.length} order(s) moved to ${config.nextStage} successfully!`
      );
      form.resetFields();
      onClose();
    } catch (error) {
      if (error?.errorFields) {
        message.error("Please fill in all required fields");
      } else {
        message.error(
          error?.data?.message || "Failed to update orders. Please try again."
        );
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setUseInhouseDriver(true);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={
        <div className="space-y-1">
          <div className="text-lg font-semibold">{config?.title}</div>
          <p className="text-sm text-gray-400 font-normal">
            {selectedOrderIds.length} order(s) selected
          </p>
        </div>
      }
      footer={null}
      destroyOnClose
      maskClosable={false}
    >
      <Form form={form} layout="vertical" className="mt-4">
        {/* Driver Selection - Only for Pickup Stage */}
        {config?.requiresDriver && (
          <>
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium">Use In-house Driver</span>
                <Switch
                  checked={useInhouseDriver}
                  onChange={setUseInhouseDriver}
                />
              </div>

              {useInhouseDriver ? (
                <Form.Item
                  name="driver_id"
                  label="Select Driver"
                  rules={[
                    { required: true, message: "Please select a driver" },
                  ]}
                >
                  <Select
                    placeholder="Choose a driver"
                    loading={driversLoading}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        {activeDrivers.length === 0 && (
                          <div className="text-center py-4 border-t">
                            <div className="text-gray-400 mb-2 text-sm">
                              No active drivers available
                            </div>
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => {
                                // onShowDriverDialog();
                                showAddDriverModal();
                                handleCancel();
                              }}
                            >
                              Add Driver
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  >
                    {activeDrivers.map((driver) => (
                      <Select.Option key={driver.id} value={driver.id}>
                        {driver.first_name} {driver.last_name} - {driver.phone}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : (
                <>
                  <Form.Item
                    name="driver_name"
                    label="Driver Name"
                    rules={[
                      { required: true, message: "Please enter driver name" },
                    ]}
                  >
                    <Input placeholder="Enter driver's full name" />
                  </Form.Item>
                  <Form.Item
                    name="driver_phone_no"
                    label="Driver Phone Number"
                    rules={[
                      { required: true, message: "Please enter phone number" },
                      {
                        pattern: /^[0-9]{11}$/,
                        message: "Enter valid 11-digit phone number",
                      },
                    ]}
                  >
                    <Input placeholder="08012345678" maxLength={11} />
                  </Form.Item>
                </>
              )}
            </div>
          </>
        )}

        {/* Comment Field */}
        <Form.Item
          name="comment"
          label={
            <span>
              Comment <span className="text-red-500">*</span>
            </span>
          }
          rules={[{ required: true, message: "Comment is required" }]}
        >
          <TextArea
            rows={4}
            placeholder="Add a comment about this status update..."
          />
        </Form.Item>

        {/* Action Buttons */}
        <div className="flex gap-2 justify-end pt-2">
          <Button
            onClick={handleCancel}
            disabled={isLoading}
            style={{
              color: "#D06D01",
              borderColor: "#D06D01",
            }}
            onMouseEnter={(e) => {
              e.target.style.color = "#D06D01";
              e.target.style.borderColor = "#D06D01";
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            style={{
              background: "linear-gradient(to right, #D06D01, #F9A022)",
              color: "white",
              border: "none",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "none";
              e.target.style.color = "#D06D01";
              e.target.style.borderColor = "#D06D01";
              e.target.style.border = "1px solid #D06D01";
            }}
            onMouseLeave={(e) => {
              e.target.style.background =
                "linear-gradient(to right, #D06D01, #F9A022)";
              e.target.style.color = "white";
              e.target.style.border = "none";
            }}
          >
            Continue →
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default BulkFulfillmentModal;
